/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import { getLogger, ILogger } from "@nuance/hux-diagnostics";
import { IViewModel, CmdBuilder } from "@nuance/hap-components";
import { BaseCommands, FormErrors } from "../../../State";
import { IRulePanelData } from "./IRulePanelData";
import { IRulePanelState } from "./IRulePanelState";

const logger: ILogger = getLogger();

export class RulePanelCommands extends BaseCommands<IRulePanelData, IRulePanelState> {
    constructor() {
        super();
        const cb = new CmdBuilder();
        const cmds = {
            onBlur: cb.bindCmd(this.OnBlurHandler),
            onTextChanged: cb.bindCmd(this.OnTextChangedHandler),
            onTargetSettingChange: cb.bindCmd(this.onTargetSettingChangeHandler),
            onToggleApplyToHidden: cb.bindCmd(this.toggleApplyToHiddenHandler),
            onToggleApplyToDisable: cb.bindCmd(this.toggleApplyToDisabledHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IRulePanelState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /**
     * Creates object containing text data to save
     * @param state Old state
     * @returns State data to save
     */
    Save = (state: IRulePanelState): IRulePanelData => {
        logger.logInformation("Saving rule properties");

        // important to make a copy!
        const saveData: IRulePanelData = {
            ...state.data
        };

        return saveData;
    };

    /**
     * Validates the form fields.
     *
     * @param state Current state (immutable)
     * @returns Form errors
     */
    Validate(state: IRulePanelState): FormErrors<IRulePanelData> {
        const errors: FormErrors<IRulePanelData> = {};

        // settingName validation
        if (state.data.ruleName === "") {
            errors.ruleName = "Rule name is required.";
        }
        if (state.data.regex === "") {
            errors.regex = "Regex is required.";
        }
        if (state.data.targetSettingName === "") {
            errors.targetSettingName = "Target setting name is required.";
        }

        return errors;
    }

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    onTargetSettingChangeHandler = (state: IRulePanelState, targetSettingName: string) => {
        // on the target setting change, hide the setting from the
        // applied to list if it matches the target setting name.
        state.data.targetSettingName = targetSettingName;

        state.data.appliedToSettings?.forEach(i => {
            if (i.settingName === targetSettingName) {
                i.hideInList = true;
            } else {
                i.hideInList = false;
            }
        });

        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);

        return state;
    };

    toggleApplyToHiddenHandler = (state: IRulePanelState, settingName: string): IRulePanelState => {
        const stateItem = state.data.appliedToSettings?.find(i => i.settingName === settingName);

        if (stateItem) {
            stateItem.isHiddenRuleApplied = !stateItem.isHiddenRuleApplied;

            if (stateItem.isHiddenRuleApplied) {
                stateItem.isDisabledRuleApplied = false;
            }
        }

        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };

    toggleApplyToDisabledHandler = (
        state: IRulePanelState,
        settingName: string
    ): IRulePanelState => {
        const stateItem = state.data.appliedToSettings?.find(i => i.settingName === settingName);

        if (stateItem) {
            stateItem.isDisabledRuleApplied = !stateItem.isDisabledRuleApplied;

            if (stateItem.isDisabledRuleApplied) {
                stateItem.isHiddenRuleApplied = false;
            }
        }

        state.isDirty = true;
        state.errors = this.Validate(state);
        state.isValid = this.IsValid(state);
        return state;
    };
}

type IRulePanelCmds = RulePanelCommands["Cmds"];
export type IRulePanelViewModel = IViewModel<IRulePanelState, IRulePanelCmds>;
